import { useCallback, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/cn";

export type SortState = { key: string; dir: "asc" | "desc" } | null;
type SortValue = string | number | Date | null | undefined;

const collator = new Intl.Collator("de-AT", {
  numeric: true,
  sensitivity: "base",
});

function compare(a: SortValue, b: SortValue): number {
  // Leere Werte stehen immer am Ende (siehe unten) — hier nur echte Werte vergleichen.
  if (a instanceof Date || b instanceof Date)
    return new Date(a as Date).getTime() - new Date(b as Date).getTime();
  if (typeof a === "number" && typeof b === "number") return a - b;
  return collator.compare(String(a), String(b));
}

/** Klick auf Spaltenkopf: aufsteigend → absteigend → ursprüngliche Reihenfolge. */
export function useSort<T>(
  rows: T[],
  accessors: Record<string, (row: T) => SortValue>,
  initial: SortState = null,
) {
  const [sort, setSort] = useState<SortState>(initial);

  const toggle = useCallback((key: string) => {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }, []);

  const sorted = useMemo(() => {
    const get = sort ? accessors[sort.key] : undefined;
    if (!sort || !get) return rows;
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((x, y) => {
      const a = get(x);
      const b = get(y);
      const aEmpty = a == null || a === "";
      const bEmpty = b == null || b === "";
      if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : aEmpty ? 1 : -1;
      return compare(a, b) * factor;
    });
    // accessors sind pro Seite konstant definiert
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);

  return { sorted, sort, toggle };
}

/** Sortierbarer Tabellenkopf. */
export function SortTh({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: React.ReactNode;
  sortKey: string;
  sort: SortState;
  onSort: (key: string) => void;
  className?: string;
}) {
  const active = sort?.key === sortKey;
  const right = className?.includes("text-right");
  return (
    <th
      className={className}
      aria-sort={
        active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1 uppercase tracking-wide hover:text-text-primary transition-colors",
          right && "flex-row-reverse",
          active && "text-text-primary",
        )}
        title="Sortieren"
      >
        {label}
        {active ? (
          sort!.dir === "asc" ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ChevronsUpDown className="w-3 h-3 opacity-40" />
        )}
      </button>
    </th>
  );
}
